// @flow

import React from 'react';
import styled from 'styled-components';
import { colors } from 'seedling/constants';

export type ToggleProps = {
  enabled: boolean,
  clickable: boolean,
  id?: string,
  value: string,
  tiny: boolean,
};

class Toggle extends React.Component<ToggleProps> {
  static defaultProps = {
    clickable: true,
    value: 'on',
    tiny: false,
  };

  renderCheckmark = () => (
    <svg
      width="15px"
      height="14px"
      viewBox="0 0 11 10"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Checkmark</title>
      <defs>
        <path
          d="M7.74102446,0.205040582 C8.04525578,-0.0843809683 8.51975872,-0.0650721456 8.80085644,0.248168045 C9.08195415,0.561408235 9.06320065,1.04996211 8.75896933,1.33938366 L3.47305862,7.79495982 C3.16967672,8.0835733 2.69675054,8.06529178 2.41524145,7.75406866 L0.20115767,5.24932692 C-0.0811395998,4.93723242 -0.064260592,4.44860609 0.238857989,4.15794947 C0.54197657,3.86729284 1.01654988,3.88467167 1.29884715,4.19676616 L3.00380162,6.13863863 L7.74102446,0.205040582 Z"
          id="path-1"
        />
      </defs>
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g transform="translate(-833.000000, -351.000000)">
          <g transform="translate(829.000000, 345.000000)">
            <g transform="translate(1.000000, 3.000000)">
              <g transform="translate(4.000000, 4.000000)">
                <mask fill="white">
                  <use xlinkHref="#path-1" />
                </mask>
                <use
                  stroke="#FFFFFF"
                  strokeWidth="0.749999448"
                  fill="#FFFFFF"
                  fillRule="nonzero"
                  xlinkHref="#path-1"
                />
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>
  );

  render() {
    const { enabled, id, value, tiny, ...otherProps } = this.props;
    return (
      <ToggleWrapper enabled={enabled} tiny={tiny} data-toggle {...otherProps}>
        {!tiny && (
          <span role="switch" aria-checked={enabled ? 'true' : 'false'}>
            <ToggleChoice choice="on" enabled={enabled}>
              {this.renderCheckmark()}
            </ToggleChoice>
          </span>
        )}
        <ToggleThumb enabled={enabled} tiny={tiny} />
        <ToggleInput
          type="checkbox"
          value={value}
          id={id}
          name={id}
          checked={enabled}
          readOnly
        />
      </ToggleWrapper>
    );
  }
}

const offBg = colors.mediumGrey;
const onBg = colors.mediumGreenhouseGreen;

const ToggleWrapper = styled.div`
  width: ${props => (props.tiny ? '30px' : '50px')};
  height: ${props => (props.tiny ? '16px' : '25px')};
  padding: 0;
  border-radius: 30px;
  background-color: ${props => (props.enabled ? onBg : offBg)};
  transition: all 0.2s ease;
  position: relative;
  user-select: none;
  ${props => (props.clickable ? '' : 'pointer-events: none;')} ${props =>
    props.clickable ? '' : 'opacity: 0.3;'}

  &:hover {
    cursor: pointer;
  }
`;

ToggleWrapper.displayName = 'ToggleWrapper';

const ToggleChoice = styled.span`
  position: absolute;
  top: 4px;
  bottom: 0px;
  margin-top: auto;
  margin-bottom: auto;
  transition: opacity 0.25s ease;
  left: 9px;
  ${props => (props.enabled ? 'opacity: 1;' : 'opacity: 0;')}
`;

ToggleChoice.displayName = 'ToggleChoice';

const ToggleThumb = styled.div`
  transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1) 0ms;
  position: absolute;
  top: 0;
  width: ${props => (props.tiny ? '16px' : '25px')};
  height: ${props => (props.tiny ? '16px' : '25px')};
  border: ${props => (props.tiny ? '3px' : '4px')} solid;
  border-radius: 50%;
  background-color: ${colors.lightestGrey};
  box-sizing: border-box;

  ${props =>
    props.enabled
      ? `
      border-color: ${onBg};
      left: ${props.tiny ? '14px' : '25px'};
    `
      : `
      border-color: ${offBg};
      left: 0;
    `};
`;

ToggleThumb.displayName = 'ToggleThumb';

const ToggleInput = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

ToggleInput.displayName = 'ToggleInput';

export default Toggle;
